import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { staffReceivingTourId, staffReceivingSteps } from '@/tours/staff';
import toast from 'react-hot-toast';
import { PackageCheck, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Minus, Plus, Download, Layers, Trash2 } from 'lucide-react';
import { Card, Button, LoadingSpinner, ErrorMessage, EmptyState } from '@/components/ui';
import { FileUploadZone, DeliveryList, ShortageForm } from '@/components/features/receiving';
import { OrderStatusBadge } from '@/components/features/orders';
import { importApi } from '@/services/api/importApi';
import { ordersApi } from '@/services/api/ordersApi';
import { warehouseApi } from '@/services/api/warehouseApi';
import { queryKeys } from '@/services/api/queryClient';
import type { ImportRecord, ImportOrder, ImportBatchDetail } from '@/types/models';
import type { OrderStatus } from '@/types/enums';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const deliveryStatusLabels: Record<string, string> = {
  pending: 'Ожидает приёмки',
  processing: 'Обработка',
  partially_received: 'Частично принята',
  received: 'Принята',
  completed: 'Завершена',
};

interface QuantityStepperProps {
  value: number;
  ordered: number;
  onChange: (value: number) => void;
  className?: string;
}

const QuantityStepper = ({ value, ordered, onChange, className }: QuantityStepperProps) => {
  const isPartial = value < ordered && value > 0;
  const isZero = value === 0;

  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      <Button
        variant="outline"
        size="sm"
        className="!p-0 !min-h-[36px] w-9 h-9 shrink-0"
        onClick={(e) => { e.stopPropagation(); onChange(Math.max(0, value - 1)); }}
        aria-label="Уменьшить"
      >
        <Minus className="w-3.5 h-3.5" />
      </Button>
      <input
        type="number"
        min={0}
        value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isNaN(n) ? 0 : Math.max(0, n));
        }}
        className={cn(
          'w-16 h-9 text-center text-sm font-medium rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          isZero && 'border-red-300 bg-red-50/50 text-red-600',
          isPartial && 'border-amber-300 bg-amber-50/50 text-amber-600',
          !isZero && !isPartial && 'border-gray-200 bg-white text-gray-900',
        )}
      />
      <Button
        variant="outline"
        size="sm"
        className="!p-0 !min-h-[36px] w-9 h-9 shrink-0"
        onClick={(e) => { e.stopPropagation(); onChange(value + 1); }}
        aria-label="Увеличить"
      >
        <Plus className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

const ReceivingPage = () => {
  useTourAutoStart({ tourId: staffReceivingTourId, steps: staffReceivingSteps });
  const queryClient = useQueryClient();
  const [selectedDelivery, setSelectedDelivery] = useState<ImportRecord | null>(null);
  const [shortageOrderId, setShortageOrderId] = useState<number | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [editedQuantities, setEditedQuantities] = useState<Record<number, number>>({});
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: queryKeys.importHistory(1),
    queryFn: () => importApi.getHistory(1, 50),
  });

  const { data: deliveryDetail, isLoading: loadingDetail } = useQuery({
    queryKey: queryKeys.importDetail(selectedDelivery?.import_id ?? 0),
    queryFn: () => importApi.getDetail(selectedDelivery!.import_id),
    enabled: !!selectedDelivery,
  });

  // Fetch batch detail when the selected delivery belongs to a batch
  const batchId = selectedDelivery?.import_batch_id ?? null;
  const { data: batchDetail } = useQuery<ImportBatchDetail>({
    queryKey: queryKeys.importBatchDetail(batchId!),
    queryFn: () => importApi.getBatchDetail(batchId!),
    enabled: batchId !== null,
  });

  // Sibling deliveries from the same batch (excluding current)
  const batchSiblings = batchDetail?.deliveries.filter(
    (d) => d.import_id !== selectedDelivery?.import_id,
  ) ?? [];

  const uploadMutation = useMutation({
    mutationFn: ({ xlsx, pdfs }: { xlsx: File; pdfs: File[] }) => importApi.upload(xlsx, pdfs),
    onSuccess: (data) => {
      toast.success(`Загружено: ${data.deliveries.length} доставок`);
      queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Ошибка при загрузке файлов'));
    },
  });

  const acceptAllMutation = useMutation({
    mutationFn: (deliveryId: number) => warehouseApi.acceptAllDelivery(deliveryId),
    onSuccess: (data) => {
      toast.success(`Принято: ${data.orders.length} заказов`);
      queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      if (selectedDelivery) {
        setSelectedDelivery({ ...selectedDelivery, status: data.delivery_status });
        queryClient.invalidateQueries({ queryKey: queryKeys.importDetail(selectedDelivery.import_id) });
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при приёмке')),
  });

  const acceptBatchMutation = useMutation({
    mutationFn: (id: number) => warehouseApi.acceptAllBatch(id),
    onSuccess: (data) => {
      const total = data.deliveries.reduce((s, d) => s + d.accepted_count, 0);
      toast.success(`Принято ${total} заказов в ${data.deliveries.length} поставках`);
      queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      if (batchId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatchDetail(batchId) });
      }
      if (selectedDelivery) {
        queryClient.invalidateQueries({ queryKey: queryKeys.importDetail(selectedDelivery.import_id) });
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при приёмке батча')),
  });

  const acceptOrderMutation = useMutation({
    mutationFn: ({ deliveryId, order }: { deliveryId: number; order: ImportOrder }) =>
      warehouseApi.acceptDelivery(
        deliveryId,
        order.items.map((item) => ({
          order_item_id: item.id,
          quantity_received: editedQuantities[item.id] ?? item.quantity_ordered,
        })),
      ),
    onSuccess: (data) => {
      toast.success('Заказ принят');
      queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      if (selectedDelivery) {
        setSelectedDelivery({ ...selectedDelivery, status: data.delivery_status });
        queryClient.invalidateQueries({ queryKey: queryKeys.importDetail(selectedDelivery.import_id) });
      }
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при приёмке заказа')),
  });

  const shortageMutation = useMutation({
    mutationFn: warehouseApi.reportShortage,
    onSuccess: () => {
      toast.success('Недостача зарегистрирована');
      setShortageOrderId(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.shortages });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при регистрации недостачи')),
  });

  const deleteMutation = useMutation({
    mutationFn: (deliveryId: number) => warehouseApi.deleteDelivery(deliveryId),
    onSuccess: (data) => {
      toast.success(`Поставка ${data.group_id} удалена`);
      setSelectedDelivery(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при удалении')),
  });

  const handleDeleteDelivery = () => {
    if (!selectedDelivery) return;
    if (!window.confirm(`Удалить поставку ${selectedDelivery.group_id}?\nВсе загруженные заказы будут удалены. Вы сможете загрузить файл заново.`)) return;
    deleteMutation.mutate(selectedDelivery.import_id);
  };

  const handleFilesSelected = (xlsx: File, pdfs: File[]) => {
    uploadMutation.mutate({ xlsx, pdfs });
  };

  const handleDownloadPdf = async () => {
    if (!selectedDelivery) return;
    setIsDownloadingPdf(true);
    try {
      const blob = await warehouseApi.downloadDeliveryPdf(selectedDelivery.import_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedDelivery.group_id}_print.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF скачан');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ошибка при скачивании PDF'));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadInvoicePdf = async (order: ImportOrder) => {
    try {
      const blob = await ordersApi.downloadInvoicePdf(order.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${order.order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Накладная скачана');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ошибка при скачивании накладной'));
    }
  };

  const toggleOrder = (orderId: number) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
        setEditedQuantities((q) => {
          const updated = { ...q };
          const order = deliveryDetail?.orders.find((o) => o.id === orderId);
          order?.items.forEach((item) => delete updated[item.id]);
          return updated;
        });
      } else {
        next.add(orderId);
        const order = deliveryDetail?.orders.find((o) => o.id === orderId);
        if (order?.status === 'ARRIVED') {
          setEditedQuantities((q) => {
            const updated = { ...q };
            order.items.forEach((item) => { updated[item.id] = item.quantity_ordered; });
            return updated;
          });
        }
      }
      return next;
    });
  };

  const deliveries = historyData?.data ?? [];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Приёмка</h1>
        <p className="text-sm text-gray-500 mt-1">Приёмка и проверка поступивших заказов</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Card>
            <Card.Header>
              <h2 className="font-semibold text-gray-900">Загрузка документов</h2>
            </Card.Header>
            <Card.Body>
              <div data-tour="staff:receiving-upload">
                <FileUploadZone
                  onFilesSelected={handleFilesSelected}
                  isUploading={uploadMutation.isPending}
                />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h2 className="font-semibold text-gray-900">Поставки</h2>
            </Card.Header>
            <Card.Body className="p-0 max-h-[400px] overflow-y-auto">
              <div data-tour="staff:receiving-list">
              {loadingHistory ? (
                <LoadingSpinner className="py-8" />
              ) : (
                <div className="p-4">
                  <DeliveryList
                    deliveries={deliveries}
                    onSelect={(d) => { setSelectedDelivery(d); setEditedQuantities({}); setExpandedOrders(new Set()); }}
                    selectedId={selectedDelivery?.import_id}
                  />
                </div>
              )}
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {selectedDelivery ? (
            <Card>
              <Card.Header>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-gray-900">
                        Поставка: {selectedDelivery.group_id}
                      </h2>

                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {selectedDelivery.orders_count} заказов • {deliveryStatusLabels[selectedDelivery.status] ?? selectedDelivery.status}
                      {batchSiblings.length > 0 && (
                        <> • ещё {batchSiblings.length} {batchSiblings.length === 1 ? 'поставка' : 'поставок'} в батче</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPdf}
                      loading={isDownloadingPdf}
                    >
                      <Download className="w-4 h-4" />
                      Скачать PDF
                    </Button>

                    {(selectedDelivery.status === 'pending' || selectedDelivery.status === 'processing') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteDelivery}
                        loading={deleteMutation.isPending}
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                        Удалить и перезагрузить
                      </Button>
                    )}

                    {selectedDelivery.status === 'received' || selectedDelivery.status === 'completed' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium">
                        <CheckCircle2 className="w-4 h-4" />
                        Всё принято
                      </span>
                    ) : (
                      <Button
                        onClick={() => acceptAllMutation.mutate(selectedDelivery.import_id)}
                        loading={acceptAllMutation.isPending}
                        size="sm"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Принять всё
                      </Button>
                    )}
                    {batchId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => acceptBatchMutation.mutate(batchId)}
                        loading={acceptBatchMutation.isPending}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      >
                        <Layers className="w-4 h-4" />
                        Принять всё в батче
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {batchSiblings.length > 0 && (
                  <div className="px-4 py-3 bg-indigo-50/40 border-b border-indigo-100">
                    <p className="text-xs font-medium text-indigo-800 mb-1.5">
                      <Layers className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                      Группы консолидации в батче
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {batchDetail!.deliveries.map((d) => (
                        <button
                          key={d.import_id}
                          onClick={() => {
                            const rec = deliveries.find((r) => r.import_id === d.import_id);
                            if (rec) {
                              setSelectedDelivery(rec);
                              setEditedQuantities({});
                              setExpandedOrders(new Set());
                            }
                          }}
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                            d.import_id === selectedDelivery?.import_id
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-100',
                          )}
                        >
                          {d.group_id}
                          <span className="opacity-70">{d.orders.length} зак.</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {loadingDetail ? (
                  <LoadingSpinner className="py-12" />
                ) : deliveryDetail ? (
                  <div className="divide-y divide-gray-100">
                    {deliveryDetail.orders.map((order) => {
                      const isExpanded = expandedOrders.has(order.id);
                      return (
                        <div key={order.id}>
                          <button
                            onClick={() => toggleOrder(order.id)}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 md:px-6 py-4 transition-colors text-left cursor-pointer',
                              isExpanded
                                ? 'bg-primary-50/40 border-l-2 border-l-primary-400'
                                : 'hover:bg-gray-50/50 border-l-2 border-l-transparent',
                            )}
                          >
                            <div className={cn(
                              'flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors',
                              isExpanded ? 'bg-primary-100' : 'bg-gray-100',
                            )}>
                              {isExpanded
                                ? <ChevronUp className="w-4 h-4 text-primary-600" />
                                : <ChevronDown className="w-4 h-4 text-gray-400" />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{order.order_number}</p>
                              {order.invoice_number && (
                                <p className="text-xs text-gray-500">Накладная: {order.invoice_number}</p>
                              )}
                            </div>
                            <OrderStatusBadge status={order.status as OrderStatus} />
                            <span className="text-xs text-gray-400">{order.items.length} поз.</span>
                          </button>

                          {isExpanded && (
                            <div className="px-3 md:px-6 pb-4 bg-gray-50/30">
                              {(() => {
                                const isShipped = order.status === 'ARRIVED';
                                const changedCount = isShipped
                                  ? order.items.filter((item) => (editedQuantities[item.id] ?? item.quantity_ordered) !== item.quantity_ordered).length
                                  : 0;

                                return (
                                  <>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm mb-3">
                                        <thead className="text-xs text-gray-500 uppercase hidden md:table-header-group">
                                          <tr>
                                            <th className="py-2 text-left">Артикул</th>
                                            <th className="py-2 text-left">Наименование</th>
                                            <th className="py-2 text-right">Заказано</th>
                                            <th className="py-2 text-right">{isShipped ? 'Факт' : 'Принято'}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {order.items.map((item, idx) => {
                                            const shortage = item.quantity_received < item.quantity_ordered;
                                            const editedQty = editedQuantities[item.id] ?? item.quantity_ordered;

                                            return (
                                              <tr
                                                key={idx}
                                                className={cn(
                                                  'flex flex-col gap-2 py-3 md:table-row md:py-0',
                                                  !isShipped && shortage && 'bg-red-50/50',
                                                )}
                                              >
                                                <td className="font-mono text-xs text-gray-600 md:py-2">{item.sku}</td>
                                                <td className="text-sm md:text-base text-gray-900 md:py-2">{item.name}</td>
                                                <td className="text-gray-500 md:py-2 md:text-right">
                                                  {item.quantity_ordered}
                                                </td>
                                                <td className="md:py-2 md:text-right">
                                                  {isShipped ? (
                                                    <div className="flex md:justify-end">
                                                      <QuantityStepper
                                                        value={editedQty}
                                                        ordered={item.quantity_ordered}
                                                        onChange={(v) => setEditedQuantities((q) => ({ ...q, [item.id]: v }))}
                                                      />
                                                    </div>
                                                  ) : (
                                                    <span className={cn(shortage && 'text-red-600 font-semibold')}>
                                                      {item.quantity_received}
                                                    </span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>

                                    {isShipped && changedCount > 0 && (
                                      <div className="mb-3 px-3 py-2 rounded-xl bg-amber-50/60 border border-amber-200/60">
                                        <p className="text-xs font-medium text-amber-700">
                                          Изменено: {changedCount} из {order.items.length} позиций
                                        </p>
                                        <div className="mt-1 space-y-0.5">
                                          {order.items
                                            .filter((item) => (editedQuantities[item.id] ?? item.quantity_ordered) < item.quantity_ordered)
                                            .map((item) => (
                                              <p key={item.id} className={cn(
                                                'text-xs',
                                                (editedQuantities[item.id] ?? item.quantity_ordered) === 0
                                                  ? 'text-red-600'
                                                  : 'text-amber-600',
                                              )}>
                                                {item.sku}: {editedQuantities[item.id] ?? item.quantity_ordered} / {item.quantity_ordered}
                                              </p>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              <div className="flex items-center gap-2 flex-wrap">
                                {order.status === 'ARRIVED' && (
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      acceptOrderMutation.mutate({
                                        deliveryId: selectedDelivery.import_id,
                                        order,
                                      })
                                    }
                                    loading={acceptOrderMutation.isPending}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Принять заказ
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShortageOrderId(shortageOrderId === order.id ? null : order.id)}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  Сообщить о недостаче
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadInvoicePdf(order)}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  Скачать накладную
                                </Button>
                              </div>

                              {shortageOrderId === order.id && (
                                <div className="mt-4 p-4 border border-red-100 rounded-xl bg-red-50/30">
                                  <ShortageForm
                                    orderId={order.id}
                                    deliveryId={selectedDelivery.import_id}
                                    orderItems={order.items.map((it) => ({
                                      id: it.id,
                                      name: it.name,
                                      sku: it.sku,
                                    }))}
                                    onSubmit={async (data) => {
                                      await shortageMutation.mutateAsync({
                                        order_id: order.id,
                                        order_item_id: data.order_item_id,
                                        consolidated_delivery_id: selectedDelivery.import_id,
                                        shortage_type: data.shortage_type as 'MISSING' | 'DAMAGED' | 'WRONG_ITEM',
                                        description: data.description,
                                      });
                                    }}
                                    isSubmitting={shortageMutation.isPending}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <ErrorMessage message="Не удалось загрузить детали" className="py-8" />
                )}
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body>
                <EmptyState
                  icon={<PackageCheck className="w-8 h-8" />}
                  title="Выберите поставку"
                  description="Выберите поставку из списка слева или загрузите новые документы"
                />
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceivingPage;
